/* eslint-disable no-unused-vars */
import store from "@/store";
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

const Autodesk = window.Autodesk;
const THREE = window.THREE;

const ChangeType = {
  PropChange: 1,
  XformChange: 2,
  GeomChange: 4
};

class CompareToolExtension extends Autodesk.Viewing.Extension {
  constructor(viewer, options) {
    super(viewer, options);
    this.viewer = viewer;
    this.compareButton = null;
    this.diffToolExtension = null;
    this.loadedModels = new Map();
    this.defaultVisibleModels = [];
    this.exitCompareButton = null;
    this.originalDiffResults = null;
    this.comparisonMode = 'POSITION_AWARE'; // 'POSITION_AWARE' | 'SHAPE_ONLY' | 'STRICT'

    // References to the two models being compared
    this.modelObjectA = null;
    this.modelObjectB = null;
    this.modelA = null;
    this.modelB = null;

    // Identity mapping caches: dbId (source) -> dbId (target)
    this.identityMapAtoB = {};
    this.identityMapBtoA = {};

    // Performance optimization caches and settings
    this.geometryCache = new Map();
    this.spatialIndexCache = new Map();
    this.tempVectors = {
      sizeA: new THREE.Vector3(),
      sizeB: new THREE.Vector3(),
      centerA: new THREE.Vector3(),
      centerB: new THREE.Vector3()
    };
    this.performanceSettings = {
      useGeometryCache: true,
      useSpatialIndex: true,
      enableParallelProcessing: true,
      chunkSize: 100,
      hashBasedOptimization: true,
      accuracyOverSpeed: false, // If true, disable all optimizations for maximum accuracy
      ...options?.performance  // Allow override
    };

    // Flag indicating whether to use identity-property (externalId) based detection
    this.useIdentityDetection = options?.useIdentityDetection || false;
    this.identityPropertyName = options?.identityPropertyName || 'externalId';
    this.usePropertyDetection = options?.usePropertyDetection || false;
  }

  static get extensionId() {
    return "CompareToolExtension";
  }

  load() {
    let existingExtensions = this.viewer.getLoadedExtensions();
    if (
      !!existingExtensions?.length &&
      this.viewer?.getLoadedExtensions()?.indexOf("Autodesk.DiffTool") !== -1
    ) {
      this.diffToolExtension = this.viewer.getExtension("Autodesk.DiffTool");
      store.state.isComparingMode = true;
    }

    // Expose for debugging
    CompareToolExtension.exposeForDebugging(this.viewer);

    return true;
  }

  unload() {
    // Exit compare mode if currently active
    if (store.state.isComparingMode) {
      this.exitCompareMode();
    }

    // Clean up any remaining UI elements
    if (this.compareButton) {
      this.removeCompareButton();
    }

    if (this.exitCompareButton) {
      this.removeExitCompareButton();
    }

    // Clean up loaded models cache
    this.loadedModels.clear();
    this.defaultVisibleModels = [];

    // Clear performance caches
    this.clearPerformanceCaches();

    return true;
  }

  removeCompareButton() {
    this.viewer.toolbar.removeControl(this.compareButton);
  }

  // Clear performance optimization caches
  clearPerformanceCaches() {
    this.geometryCache.clear();
    this.spatialIndexCache.clear();
  }

  // Custom computation function to force geometric comparison
  createCustomCompute() {
    return {
      id: 'GeometricComparison',
      computeDiff: async (modelA, modelB, cancellationToken, progress) => {
        try {
          return this.performCustomGeometricAnalysis(modelA, modelB, progress);

        } catch (error) {
          debugError('Custom geometric comparison failed:', error);
          // Return null to fallback to default computation
          return null;
        }
      }
    };
  }

  // Get DiffComputation module using multiple approaches
  getDiffComputationModule() {
    try {
      // Try multiple approaches to access DiffComputation

      // 1. Try global Autodesk namespace
      if (window.Autodesk?.Viewing?.Private?.DiffComputation) {
        return window.Autodesk.Viewing.Private.DiffComputation;
      }

      // 2. Try accessing through the loaded extension (if available)
      if (this.diffToolExtension && this.diffToolExtension.constructor) {
        const extensionClass = this.diffToolExtension.constructor;
        // Look for DiffComputation in the extension's dependencies
        const modules = extensionClass.modules || extensionClass.__modules__ || {};
        if (modules.DiffComputation) {
          return modules.DiffComputation;
        }
      }

      // 3. Try accessing through viewer extensions
      const loadedExtensions = this.viewer.getLoadedExtensions();
      for (const extName of loadedExtensions) {
        const ext = this.viewer.getExtension(extName);
        if (ext && ext.DiffComputation) {
          return ext.DiffComputation;
        }
      }

      // 4. Try accessing through extension manager
      if (window.Autodesk?.Viewing?.theExtensionManager?.extensions) {
        const extensions = window.Autodesk.Viewing.theExtensionManager.extensions;
        for (const [name, extClass] of Object.entries(extensions)) {
          if (name.includes('Diff') && extClass.DiffComputation) {
            return extClass.DiffComputation;
          }
        }
      }

      return null;
    } catch (error) {
      debugWarn('Error accessing DiffComputation module:', error);
      return null;
    }
  }

  // Perform custom geometric analysis without relying on DiffComputation
  async performCustomGeometricAnalysis(modelA, modelB, progress) {
    try {
      if (progress) progress(10);

      // Optionally detect translation modifications using identity property first
      let identityModifications = {
        modifiedPairs: [],
        modifiedTriplets: [],
        modifiedA: [],
        modifiedB: []
      };

      if (this.useIdentityDetection && this.identityPropertyName) {
        identityModifications = await this.detectTranslationModificationsUsingIdentityProperty(modelA, modelB, this.identityPropertyName, this.usePropertyDetection);
      }

      // Get all dbIds from both models
      const allDbIdsA = this.getAllDbIds(modelA).filter(id => !identityModifications.modifiedA.includes(id));
      const allDbIdsB = this.getAllDbIds(modelB).filter(id => !identityModifications.modifiedB.includes(id));

      if (progress) progress(30);

      // Perform geometric comparison to find added/removed items
      const geometricAdded = await this.findGeometricallyAdded(allDbIdsA, allDbIdsB, modelA, modelB);

      if (progress) progress(60);

      const geometricRemoved = await this.findGeometricallyRemoved(allDbIdsA, allDbIdsB, modelA, modelB);

      if (progress) progress(90);

      // Create a basic diff result structure
      const result = {
        added: geometricAdded,
        removed: geometricRemoved,
        matched: identityModifications.modifiedTriplets,
        identityModified: identityModifications.modifiedPairs,
        total: allDbIdsA.length,
        geometricAdded: geometricAdded,
        geometricRemoved: geometricRemoved,
        vA: modelA.getData().basePath,
        vB: modelB.getData().basePath,
        supportViewableIdx: [],
        loadedSupportModels: []
      };

      // Store results
      this.originalDiffResults = result;

      if (progress) progress(100);

      return result;

    } catch (error) {
      debugError('Error in custom geometric analysis:', error);
      throw error;
    }
  }

  // Enhance results with additional geometric comparison
  async enhanceWithGeometricComparison(result, modelA, modelB) {
    try {
      // Get all dbIds from both models
      const allDbIdsA = this.getAllDbIds(modelA);
      const allDbIdsB = this.getAllDbIds(modelB);

      // Perform geometric comparison to find truly added/removed items
      const geometricAdded = await this.findGeometricallyAdded(allDbIdsA, allDbIdsB, modelA, modelB);
      const geometricRemoved = await this.findGeometricallyRemoved(allDbIdsA, allDbIdsB, modelA, modelB);

      // Merge with existing results
      const enhancedAdded = this.mergeUnique(result.added, geometricAdded);
      const enhancedRemoved = this.mergeUnique(result.removed, geometricRemoved);

      return {
        ...result,
        added: enhancedAdded,
        removed: enhancedRemoved,
        geometricAdded: geometricAdded,
        geometricRemoved: geometricRemoved,
        originalAdded: result.added,
        originalRemoved: result.removed
      };
    } catch (error) {
      debugError('Error enhancing with geometric comparison:', error);
      return result;
    }
  }

  // Get all dbIds from a model
  getAllDbIds(model) {
    const dbIds = [];
    const tree = model.getInstanceTree();
    if (tree) {
      tree.enumNodeChildren(tree.getRootId(), (dbId) => {
        dbIds.push(dbId);
      }, true);
    }

    return dbIds;
  }

  // Debug method to inspect specific dbIds
  debugSpecificDbIds(modelA, modelB, targetDbIds = [120, 122]) {
    const allDbIdsA = this.getAllDbIds(modelA);
    const allDbIdsB = this.getAllDbIds(modelB);
    const results = {};

    for (const dbId of targetDbIds) {
      const inModelA = allDbIdsA.includes(dbId);
      const inModelB = allDbIdsB.includes(dbId);

      results[dbId] = {
        inModelA,
        inModelB,
        expectedResult: inModelA && !inModelB ? 'ADDED' :
          !inModelA && inModelB ? 'REMOVED' :
            inModelA && inModelB ? 'MATCHED/MODIFIED' : 'NOT_FOUND'
      };

      // If present in both models, test geometric comparison
      if (inModelA && inModelB) {
        const geomA = this.getObjectGeometry(modelA, dbId);
        const geomB = this.getObjectGeometry(modelB, dbId);

        if (geomA && geomB) {
          results[dbId].comparison = {
            withPosition: this.compareGeometries(geomA, geomB, true),
            shapeOnly: this.compareGeometries(geomA, geomB, false),
            currentMode: this.compareGeometriesByMode(geomA, geomB),
            distance: geomA.center && geomB.center ?
              geomA.center.distanceTo(geomB.center).toFixed(3) : 'N/A'
          };
        }
      }
    }

    console.table(results);
    return results;
  }

  // Debug object details
  debugObjectDetails(model, dbId) {
    try {
      const tree = model.getInstanceTree();
      if (!tree) {
        return { error: 'No instance tree' };
      }

      const name = tree.getNodeName(dbId);
      const parent = tree.getNodeParentId(dbId);
      const children = [];
      tree.enumNodeChildren(dbId, (childId) => children.push(childId));

      const fragIds = [];
      tree.enumNodeFragments(dbId, (fragId) => fragIds.push(fragId));

      return {
        name,
        parent,
        childrenCount: children.length,
        fragCount: fragIds.length,
        isLeaf: children.length === 0
      };

    } catch (error) {
      debugError(`Error debugging object details for dbId ${dbId}:`, error);
      return { error: error.message };
    }
  }

  // Find geometrically added objects (optimized with spatial indexing)
  async findGeometricallyAdded(dbIdsA, dbIdsB, modelA, modelB) {
    // Force original method if accuracy is prioritized
    if (this.performanceSettings.accuracyOverSpeed) {
      return this.findGeometricallyAddedOriginal(dbIdsA, dbIdsB, modelA, modelB);
    }

    // Use optimized spatial indexing if enabled
    if (this.performanceSettings.useSpatialIndex) {
      return this.findGeometricallyAddedOptimized(dbIdsA, dbIdsB, modelA, modelB);
    }

    // Fallback to original method
    return this.findGeometricallyAddedOriginal(dbIdsA, dbIdsB, modelA, modelB);
  }

  // Optimized version using spatial indexing and parallel processing
  async findGeometricallyAddedOptimized(dbIdsA, dbIdsB, modelA, modelB) {
    // Build spatial index for model B
    const indexB = this.buildGeometryIndex(modelB, dbIdsB);

    // Use parallel processing if enabled and dataset is large enough
    if (this.performanceSettings.enableParallelProcessing && dbIdsA.length > this.performanceSettings.chunkSize) {
      const chunks = this.chunkArray(dbIdsA, this.performanceSettings.chunkSize);

      const promises = chunks.map(chunk =>
        this.processChunkForAdded(chunk, indexB, modelA)
      );

      const results = await Promise.all(promises);
      return results.flat();
    }

    // Sequential processing for smaller datasets
    const added = [];
    for (const dbIdA of dbIdsA) {
      const geomA = this.getObjectGeometry(modelA, dbIdA);
      if (!geomA) continue;

      const matchDbId = this.getGeometricMatchDbId(geomA, indexB);
      if (matchDbId != null) {
        this.addIdentityMapping(dbIdA, matchDbId);
      } else {
        added.push(dbIdA);
      }
    }

    return added;
  }

  // Original O(n*m) method as fallback
  findGeometricallyAddedOriginal(dbIdsA, dbIdsB, modelA, modelB) {
    const added = [];

    for (const dbIdA of dbIdsA) {
      const geometryA = this.getObjectGeometry(modelA, dbIdA);
      if (!geometryA) {
        continue;
      }

      let matchedDbId = null;

      for (const dbIdB of dbIdsB) {
        const geometryB = this.getObjectGeometry(modelB, dbIdB);
        if (geometryB) {
          const matches = this.compareGeometriesByMode(geometryA, geometryB);
          if (matches) {
            matchedDbId = dbIdB;
            break;
          }
        }
      }

      if (matchedDbId == null) {
        added.push(dbIdA);
      } else {
        this.addIdentityMapping(dbIdA, matchedDbId);
      }
    }

    return added;
  }

  // Find geometrically removed objects (optimized with spatial indexing)
  async findGeometricallyRemoved(dbIdsA, dbIdsB, modelA, modelB) {
    // Force original method if accuracy is prioritized
    if (this.performanceSettings.accuracyOverSpeed) {
      return this.findGeometricallyRemovedOriginal(dbIdsA, dbIdsB, modelA, modelB);
    }

    // Use optimized spatial indexing if enabled
    if (this.performanceSettings.useSpatialIndex) {
      return this.findGeometricallyRemovedOptimized(dbIdsA, dbIdsB, modelA, modelB);
    }

    // Fallback to original method
    return this.findGeometricallyRemovedOriginal(dbIdsA, dbIdsB, modelA, modelB);
  }

  // Optimized version using spatial indexing and parallel processing
  async findGeometricallyRemovedOptimized(dbIdsA, dbIdsB, modelA, modelB) {
    // Build spatial index for model A
    const indexA = this.buildGeometryIndex(modelA, dbIdsA);

    // Use parallel processing if enabled and dataset is large enough
    if (this.performanceSettings.enableParallelProcessing && dbIdsB.length > this.performanceSettings.chunkSize) {
      const chunks = this.chunkArray(dbIdsB, this.performanceSettings.chunkSize);

      const promises = chunks.map(chunk =>
        this.processChunkForRemoved(chunk, indexA, modelB)
      );

      const results = await Promise.all(promises);
      return results.flat();
    }

    // Sequential processing for smaller datasets
    const removed = [];
    for (const dbIdB of dbIdsB) {
      const geomB = this.getObjectGeometry(modelB, dbIdB);
      if (!geomB) continue;

      const matchDbId = this.getGeometricMatchDbId(geomB, indexA);
      if (matchDbId != null) {
        this.addIdentityMapping(matchDbId, dbIdB);
      } else {
        removed.push(dbIdB);
      }
    }

    return removed;
  }

  // Process chunk of dbIds for removed items
  async processChunkForRemoved(chunk, indexA, modelB) {
    const removed = [];

    for (const dbIdB of chunk) {
      const geomB = this.getObjectGeometry(modelB, dbIdB);
      if (!geomB) continue;

      const matchDbId = this.getGeometricMatchDbId(geomB, indexA);
      if (matchDbId != null) {
        this.addIdentityMapping(matchDbId, dbIdB);
      } else {
        removed.push(dbIdB);
      }
    }

    return removed;
  }

  // Original O(n*m) method as fallback
  findGeometricallyRemovedOriginal(dbIdsA, dbIdsB, modelA, modelB) {
    const removed = [];

    for (const dbIdB of dbIdsB) {
      const geometryB = this.getObjectGeometry(modelB, dbIdB);
      if (!geometryB) {
        continue;
      }

      let matchedDbId = null;

      for (const dbIdA of dbIdsA) {
        const geometryA = this.getObjectGeometry(modelA, dbIdA);
        if (geometryA) {
          const matches = this.compareGeometriesByMode(geometryA, geometryB);
          if (matches) {
            matchedDbId = dbIdA;
            break;
          }
        }
      }

      if (matchedDbId == null) {
        removed.push(dbIdB);
      } else {
        this.addIdentityMapping(matchedDbId, dbIdB);
      }
    }

    return removed;
  }

  // Get object geometry information for comparison (with caching)
  getObjectGeometry(model, dbId) {
    // Force fresh computation if accuracy is prioritized or caching is disabled
    if (this.performanceSettings.accuracyOverSpeed || !this.performanceSettings.useGeometryCache) {
      return this.computeObjectGeometry(model, dbId);
    }

    const cacheKey = `${model.getData().basePath}_${dbId}`;

    if (this.geometryCache.has(cacheKey)) {
      return this.geometryCache.get(cacheKey);
    }

    const geometry = this.computeObjectGeometry(model, dbId);
    this.geometryCache.set(cacheKey, geometry);
    return geometry;
  }

  // Compute object geometry (actual computation without caching)
  computeObjectGeometry(model, dbId) {
    try {
      const fragIds = [];
      const tree = model.getInstanceTree();

      tree.enumNodeFragments(dbId, (fragId) => {
        fragIds.push(fragId);
      });

      if (fragIds.length === 0) return null;

      const fragList = model.getFragmentList();
      const geometry = {
        fragCount: fragIds.length,
        bounds: new THREE.Box3(),
        hashes: [],
        center: null,
        size: null,
        sizeKey: null // For spatial indexing
      };

      // Collect geometry hashes and bounds
      for (const fragId of fragIds) {
        const geom = fragList.getGeometry(fragId);
        if (geom) {
          geometry.hashes.push(geom.hash || 'no-hash');

          // Get fragment bounds
          const fragBounds = new THREE.Box3();
          fragList.getWorldBounds(fragId, fragBounds);
          geometry.bounds.union(fragBounds);
        }
      }

      // Calculate center and size for easy access
      if (!geometry.bounds.isEmpty()) {
        geometry.center = geometry.bounds.getCenter(new THREE.Vector3());
        geometry.size = geometry.bounds.getSize(new THREE.Vector3());
        geometry.sizeKey = this.getSizeKey(geometry.size);
      }

      return geometry;
    } catch (error) {
      debugWarn('Error getting geometry for dbId:', dbId, error);
      return null;
    }
  }

  // Generate size key for spatial indexing (with broader tolerance for accuracy)
  getSizeKey(size, precision = 10) {
    if (!size) return 'null';
    // Use lower precision to create broader size buckets, ensuring similar objects are grouped
    return `${Math.round(size.x * precision)}_${Math.round(size.y * precision)}_${Math.round(size.z * precision)}`;
  }

  // Generate multiple size keys for fuzzy spatial matching
  getFuzzySizeKeys(size, precision = 10) {
    if (!size) return ['null'];

    const baseX = Math.round(size.x * precision);
    const baseY = Math.round(size.y * precision);
    const baseZ = Math.round(size.z * precision);

    // Generate neighboring keys to catch objects on bucket boundaries
    const keys = new Set();
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          keys.add(`${baseX + dx}_${baseY + dy}_${baseZ + dz}`);
        }
      }
    }

    return Array.from(keys);
  }

  // Compare two geometry objects (optimized with early exits and memory pooling)
  compareGeometries(geomA, geomB, includePosition = true) {
    if (!geomA || !geomB) return false;

    // Fragment count check first (fastest)
    if (geomA.fragCount !== geomB.fragCount) return false;

    const tolerance = 0.001 / this.viewer.model.getUnitScale();

    // Size comparison using pre-calculated size or fresh calculation (medium cost)
    let sizesMatch = false;
    if (geomA.size && geomB.size) {
      // Use pre-calculated sizes if available
      sizesMatch = Math.abs(geomA.size.x - geomB.size.x) <= tolerance &&
        Math.abs(geomA.size.y - geomB.size.y) <= tolerance &&
        Math.abs(geomA.size.z - geomB.size.z) <= tolerance;
    } else {
      // Fallback to bounds calculation (avoid memory pooling to prevent conflicts)
      const sizeA = geomA.bounds.getSize(new THREE.Vector3());
      const sizeB = geomB.bounds.getSize(new THREE.Vector3());

      sizesMatch = Math.abs(sizeA.x - sizeB.x) <= tolerance &&
        Math.abs(sizeA.y - sizeB.y) <= tolerance &&
        Math.abs(sizeA.z - sizeB.z) <= tolerance;
    }

    if (!sizesMatch) return false;

    // Hash comparison as additional verification (not early exit) 
    if (this.performanceSettings.hashBasedOptimization &&
      geomA.hashes.length > 0 && geomB.hashes.length > 0) {
      const validHashesA = geomA.hashes.filter(h => h !== 'no-hash');
      const validHashesB = geomB.hashes.filter(h => h !== 'no-hash');

      if (validHashesA.length > 0 && validHashesB.length > 0) {
        const setA = new Set(validHashesA);
        const setB = new Set(validHashesB);

        // If hashes don't match, geometries are definitely different
        if (setA.size !== setB.size) return false;
        for (const hash of setA) {
          if (!setB.has(hash)) return false;
        }
      // Hashes match - continue with position check if needed
      }
    }

    // Position comparison last (if needed)
    return !includePosition || this.comparePositions(geomA, geomB);
  }

  // Optimized position comparison (avoiding memory pooling conflicts)
  comparePositions(geomA, geomB) {
    const positionTolerance = 0.01;

    if (geomA.center && geomB.center) {
      // Use pre-calculated centers if available
      return Math.abs(geomA.center.x - geomB.center.x) <= positionTolerance &&
        Math.abs(geomA.center.y - geomB.center.y) <= positionTolerance &&
        Math.abs(geomA.center.z - geomB.center.z) <= positionTolerance;
    } else {
      // Fallback to bounds calculation (create new vectors to avoid conflicts)
      const centerA = geomA.bounds.getCenter(new THREE.Vector3());
      const centerB = geomB.bounds.getCenter(new THREE.Vector3());

      return Math.abs(centerA.x - centerB.x) <= positionTolerance &&
        Math.abs(centerA.y - centerB.y) <= positionTolerance &&
        Math.abs(centerA.z - centerB.z) <= positionTolerance;
    }
  }

  // Compare geometries based on current mode
  compareGeometriesByMode(geomA, geomB) {
    switch (this.comparisonMode) {
      case 'SHAPE_ONLY':
        // Only compare shape/size, ignore position
        return this.compareGeometries(geomA, geomB, false);

      case 'POSITION_AWARE':
        // Compare both shape and position (default)
        return this.compareGeometries(geomA, geomB, true);

      case 'STRICT':
        // Most strict comparison including exact position match
        return this.compareGeometriesStrict(geomA, geomB);

      default:
        return this.compareGeometries(geomA, geomB, true);
    }
  }

  // Strict geometric comparison with tighter tolerances
  compareGeometriesStrict(geomA, geomB) {
    if (!geomA || !geomB) return false;

    // Compare fragment count
    if (geomA.fragCount !== geomB.fragCount) return false;

    const strictTolerance = 0.0001; // Much tighter tolerance

    // Compare bounds dimensions (size) with strict tolerance
    const sizeA = geomA.bounds.getSize(new THREE.Vector3());
    const sizeB = geomB.bounds.getSize(new THREE.Vector3());

    if (Math.abs(sizeA.x - sizeB.x) > strictTolerance ||
      Math.abs(sizeA.y - sizeB.y) > strictTolerance ||
      Math.abs(sizeA.z - sizeB.z) > strictTolerance) {
      return false;
    }

    // Compare position with strict tolerance
    const centerA = geomA.bounds.getCenter(new THREE.Vector3());
    const centerB = geomB.bounds.getCenter(new THREE.Vector3());

    if (Math.abs(centerA.x - centerB.x) > strictTolerance ||
      Math.abs(centerA.y - centerB.y) > strictTolerance ||
      Math.abs(centerA.z - centerB.z) > strictTolerance) {
      return false;
    }

    // All geometry hashes must match exactly
    if (geomA.hashes.length !== geomB.hashes.length) return false;

    const sortedHashesA = [...geomA.hashes].sort();
    const sortedHashesB = [...geomB.hashes].sort();

    for (let i = 0; i < sortedHashesA.length; i++) {
      if (sortedHashesA[i] !== sortedHashesB[i]) {
        return false;
      }
    }

    return true;
  }

  // Set comparison mode
  setComparisonMode(mode) {
    const validModes = ['SHAPE_ONLY', 'POSITION_AWARE', 'STRICT'];
    if (!validModes.includes(mode)) {
      debugError('Invalid comparison mode. Valid modes:', validModes);
      return;
    }

    this.comparisonMode = mode;
  }

  // Get current comparison mode
  getComparisonMode() {
    return this.comparisonMode;
  }

  // Get performance statistics
  getPerformanceStats() {
    return {
      geometryCacheSize: this.geometryCache.size,
      spatialIndexCacheSize: this.spatialIndexCache.size,
      settings: this.performanceSettings
    };
  }

  // Configure performance settings
  configurePerformance(settings) {
    this.performanceSettings = { ...this.performanceSettings, ...settings };

    // Clear caches if indexing is disabled
    if (!settings.useSpatialIndex) {
      this.spatialIndexCache.clear();
    }
    if (!settings.useGeometryCache) {
      this.geometryCache.clear();
    }
  }

  // Build spatial index for efficient geometric lookups
  buildGeometryIndex(model, dbIds) {
    // Create more unique cache key using model path and sorted dbIds hash
    const sortedIds = [...dbIds].sort((a, b) => a - b);
    const idsHash = sortedIds.slice(0, 10).join(',') + (sortedIds.length > 10 ? `...${sortedIds.length}` : '');
    const cacheKey = `${model.getData().basePath}_${idsHash}`;

    if (this.performanceSettings.useSpatialIndex && this.spatialIndexCache.has(cacheKey)) {
      return this.spatialIndexCache.get(cacheKey);
    }

    const spatialIndex = new Map(); // size -> [dbIds]
    const hashIndex = new Map();    // hash -> [dbIds]
    const geometries = new Map();   // dbId -> geometry

    for (const dbId of dbIds) {
      const geom = this.getObjectGeometry(model, dbId);
      if (!geom) continue;

      geometries.set(dbId, geom);

      // Index by size with fuzzy keys (for broader size-based matching)
      const sizeKeys = this.getFuzzySizeKeys(geom.size);
      for (const sizeKey of sizeKeys) {
        if (!spatialIndex.has(sizeKey)) spatialIndex.set(sizeKey, []);
        spatialIndex.get(sizeKey).push({ dbId, geom });
      }

      // Index by geometry hash (for exact matches)
      if (this.performanceSettings.hashBasedOptimization && geom.hashes.length > 0) {
        const validHashes = geom.hashes.filter(h => h !== 'no-hash');
        if (validHashes.length > 0) {
          const hashKey = validHashes.sort().join('|');
          if (!hashIndex.has(hashKey)) hashIndex.set(hashKey, []);
          hashIndex.get(hashKey).push({ dbId, geom });
        }
      }
    }

    const index = { spatialIndex, hashIndex, geometries };

    if (this.performanceSettings.useSpatialIndex) {
      this.spatialIndexCache.set(cacheKey, index);
    }

    return index;
  }

  // Chunk array for parallel processing
  chunkArray(array, chunkSize) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  // Process chunk of dbIds in parallel
  async processChunkForAdded(chunk, indexB, modelA) {
    const added = [];

    for (const dbIdA of chunk) {
      const geomA = this.getObjectGeometry(modelA, dbIdA);
      if (!geomA) continue;

      const matchDbId = this.getGeometricMatchDbId(geomA, indexB);
      if (matchDbId != null) {
        this.addIdentityMapping(dbIdA, matchDbId);
      } else {
        added.push(dbIdA);
      }
    }

    return added;
  }

  // Check if geometry has a match in the index
  hasGeometricMatch(geomA, indexB) {
    // Hash-based lookup for potential quick match
    if (this.performanceSettings.hashBasedOptimization && geomA.hashes.length > 0) {
      const validHashes = geomA.hashes.filter(h => h !== 'no-hash');
      if (validHashes.length > 0) {
        const hashKey = validHashes.sort().join('|');
        if (indexB.hashIndex.has(hashKey)) {
          // Found potential hash match - still verify with geometric comparison
          const hashCandidates = indexB.hashIndex.get(hashKey);
          const hashMatch = hashCandidates.some(candidate =>
            this.compareGeometriesByMode(geomA, candidate.geom)
          );
          if (hashMatch) return true;
        }
      }
    }

    // Fuzzy spatial lookup for size-similar objects
    const sizeKeys = this.getFuzzySizeKeys(geomA.size);
    const allCandidates = new Set();

    // Collect candidates from all fuzzy size buckets
    for (const sizeKey of sizeKeys) {
      const candidates = indexB.spatialIndex.get(sizeKey) || [];
      candidates.forEach(candidate => allCandidates.add(candidate));
    }

    // Check geometric comparison with all size-similar candidates
    return Array.from(allCandidates).some(candidate =>
      this.compareGeometriesByMode(geomA, candidate.geom)
    );
  }

  // Merge arrays and remove duplicates
  mergeUnique(arr1, arr2) {
    const merged = [...arr1];
    for (const item of arr2) {
      if (!merged.includes(item)) {
        merged.push(item);
      }
    }
    return merged;
  }

  // Inject custom computation into diff results after computation
  injectGeometricResults(diffExtension) {
    if (!diffExtension || !diffExtension.computedDiffs) {
      return;
    }

    try {
      // Modify the computed diffs to include our geometric results
      for (let i = 0; i < diffExtension.computedDiffs.length; i++) {
        const diff = diffExtension.computedDiffs[i];

        if (this.originalDiffResults) {
          // Add our geometric comparison results
          diff.geometricAdded = this.originalDiffResults.geometricAdded || [];
          diff.geometricRemoved = this.originalDiffResults.geometricRemoved || [];

          // Update the main added/removed arrays
          if (this.originalDiffResults.added) {
            diff.added = [...new Set([...diff.added, ...this.originalDiffResults.added])];
          }
          if (this.originalDiffResults.removed) {
            diff.removed = [...new Set([...diff.removed, ...this.originalDiffResults.removed])];
          }
        }
      }

      // Trigger re-rendering of the changes panel if it exists
      if (diffExtension.changesPanel) {
        diffExtension.changesPanel.renderChangesPanel();
      }

    } catch (error) {
      debugError('Error injecting geometric results:', error);
    }
  }

  // Fallback geometric analysis when DiffTool fails to initialize
  async performFallbackGeometricAnalysis(modelA, modelB) {
    try {
      // Get the loaded models from the viewer
      const models = this.viewer.getAllModels();
      const model1 = models.find(m => m === modelA) || models[models.length - 2];
      const model2 = models.find(m => m === modelB) || models[models.length - 1];

      if (!model1 || !model2) {
        debugError('Could not find loaded models for fallback analysis');
        return;
      }

      // Perform our custom geometric analysis
      const result = await this.performCustomGeometricAnalysis(model1, model2);

      // Create a simple visualization of results
      this.visualizeFallbackResults(result, model1, model2);

    } catch (error) {
      debugError('Error in fallback geometric analysis:', error);
    }
  }

  // Simple visualization of fallback results
  visualizeFallbackResults(result, modelA, modelB) {
    try {
      // Apply basic theming to show differences
      const addedColor = new THREE.Vector4(0, 1, 0, 1); // Green for added
      const removedColor = new THREE.Vector4(1, 0, 0, 1); // Red for removed

      // Theme added objects
      result.added.forEach(dbId => {
        try {
          modelA.setThemingColor(dbId, addedColor);
        } catch (error) {
          debugWarn('Error theming added object:', dbId, error);
        }
      });

      // Theme removed objects
      result.removed.forEach(dbId => {
        try {
          modelB.setThemingColor(dbId, removedColor);
        } catch (error) {
          debugWarn('Error theming removed object:', dbId, error);
        }
      });

      // Invalidate to refresh the view
      this.viewer.impl.invalidate(true);

    } catch (error) {
      debugError('Error visualizing fallback results:', error);
    }
  }

  async startCompare(compareNode1, compareNode2, extraOptions = {}) {
    try {
      debugLog('Starting comparison between:', compareNode1.name, 'and', compareNode2.name);

      // Merge extra options
      this.useIdentityDetection = !!extraOptions.useIdentityDetection;
      if (extraOptions.identityPropertyName) {
        this.identityPropertyName = extraOptions.identityPropertyName;
      }

      if (typeof extraOptions.usePropertyDetection === 'boolean') {
        this.usePropertyDetection = extraOptions.usePropertyDetection;
      }

      // Validate input parameters
      if (!compareNode1 || !compareNode2) {
        throw new Error('Both comparison nodes are required');
      }

      if (!compareNode1.urn || !compareNode2.urn) {
        throw new Error('Both models must have valid URNs');
      }

      if (!compareNode1.node?.guid || !compareNode2.node?.guid) {
        throw new Error('Both models must have valid view IDs');
      }

      // Set comparison mode
      store.state.isComparingMode = true;

      // Store current visible models to restore later
      this.defaultVisibleModels = this.viewer.getVisibleModels();

      // Hide all current models
      this.hideAllModels();

      // Load models and set up views
      const model1 = await this.loadModelAndView(compareNode1);
      debugLog(
        `%c✅ Model A (${compareNode1.name}) is loaded`,
        `color: green; font-size: 16px; font-weight: bold;`
      );

      const model2 = await this.loadModelAndView(compareNode2);
      debugLog(
        `%c✅ Model B (${compareNode2.name}) is loaded`,
        `color: green; font-size: 16px; font-weight: bold;`
      );

      // Hide default visible models
      this.defaultVisibleModels.forEach(m => {
        this.viewer.hideModel(m)
      })      

      await new Promise(resolve => setTimeout(resolve, 1000));

      // Store references for mapping functions
      this.modelObjectA = compareNode1.object;
      this.modelObjectB = compareNode2.object;

      this.modelA = model1;
      this.modelB = model2;

      // Validate models were loaded successfully
      if (!model1 || !model2) {
        throw new Error('Failed to load one or both models for comparison');
      }

      const config = {
        primaryModels: [model1],
        diffModels: [model2],
        versionA: compareNode1.name,
        versionB: compareNode2.name,
        mimeType: "application/vnd.autodesk.navisworks",
        diffMode: "overlay",
        isOTGCompareDisabled: true, // Force disable OTG comparison
        customCompute: this.createCustomCompute(), // Use our custom computation
        onInitialized: () => {
          // Inject our geometric results after initialization
          setTimeout(() => {
            this.injectGeometricResults(this.diffToolExtension);
          }, 1000);
        },
        onError: (error) => {
          debugError('DiffTool initialization error:', error);
          // Fallback: perform our own geometric analysis
          this.performFallbackGeometricAnalysis(model1, model2);
        }
      };

      // Remember mimeType for property filtering helpers
      this.currentMimeType = config.mimeType;

      await this.loadCompareTool(config);

    } catch (error) {
      debugError('Error in startCompare:', error);

      // Reset comparison mode on error
      store.state.isComparingMode = false;

      // Restore original models if comparison failed
      if (this.defaultVisibleModels?.length > 0) {
        this.defaultVisibleModels.forEach((model) => {
          this.viewer.showModel(model);
        });
      }

      // Re-throw the error so calling code can handle it
      throw error;
    }
  }

  async loadCompareTool(config) {
    // Load Autodesk.DiffTool extension if not already loaded
    this.diffToolExtension = await this.viewer.loadExtension(
      "Autodesk.DiffTool",
      config
    );

    this.diffToolExtension.THEME = {
      added: new THREE.Vector4(0, 1, 0, 1),
      removed: new THREE.Vector4(1, 0, 0, 1),
      modified: new THREE.Vector4(1, 1, 0, 1),
    }

    if (this.diffToolExtension) {
      const { viewerToolbarDiffGroup, toolbarControls } =
        this.diffToolExtension;

      // Adjust diff tool buttons click
      const originalHandler = toolbarControls.sideBySideButton.onClick;
      toolbarControls.sideBySideButton.onClick = () => {
        if (this.diffToolExtension.diffMode === "sidebyside") {
          toolbarControls.overlayButton.onClick();
          return;
        }

        originalHandler();
      };

      // Add exit compare mode button
      this.addExitCompareButton(viewerToolbarDiffGroup);

      // Hook into the extension to monitor diff completion
      this.monitorDiffCompletion();
    }
  }

  // Monitor when diff computation is completed
  monitorDiffCompletion() {
    if (!this.diffToolExtension) return;

    // Override the init method to hook into completion
    const originalInit = this.diffToolExtension.init.bind(this.diffToolExtension);
    this.diffToolExtension.init = async (...args) => {
      const result = await originalInit(...args);

      // After diff is computed, inject our geometric results
      setTimeout(() => {
        this.injectGeometricResults(this.diffToolExtension);
      }, 500);

      return result;
    };
  }

  addExitCompareButton(toolbarGroup) {
    if (this.exitCompareButton) return; // Button already exists

    this.exitCompareButton = new Autodesk.Viewing.UI.Button('toolbar-exitCompareButton');
    this.exitCompareButton.setToolTip('Exit Compare Mode');
    this.exitCompareButton.setIcon('adsk-icon-exit');
    this.exitCompareButton.onClick = () => {
      this.exitCompareMode();
    };

    toolbarGroup.addControl(this.exitCompareButton);
  }

  removeExitCompareButton() {
    if (this.exitCompareButton && this.diffToolExtension) {
      const { viewerToolbarDiffGroup } = this.diffToolExtension;
      if (viewerToolbarDiffGroup) {
        viewerToolbarDiffGroup.removeControl(this.exitCompareButton);
      }
      this.exitCompareButton = null;
    }
  }

  hideAllModels() {
    const models = this.viewer.getAllModels();
    models.forEach((model) => {
      this.viewer.hideModel(model);
    });
  }

  async loadModelAndView(compareNode) {
    return new Promise((resolve) => {
      const { urn, node } = compareNode;
      const modelKey = `${urn}_${node.guid}`;

      let model;
      if (this.loadedModels.has(modelKey)) {
        model = this.loadedModels.get(modelKey);
        this.viewer.showModel(model);

        setTimeout(() => {
          resolve(model);
        }, 500);
      } else {
        Autodesk.Viewing.Document.load(
          `urn:${urn}`,
          (doc) => {
            this.onDocumentLoadSuccess(doc, node.guid, modelKey).then(resolve);
          },
          this.onDocumentLoadFailure
        );
      }
    });
  }

  async onDocumentLoadSuccess(doc, viewId, modelKey) {
    return new Promise((resolve, reject) => {
      const options = {
        // preserveView: true,
        keepCurrentModels: true,
        modelSpace: true,
        applyScaling: { to: store.getters['getDisplayUnit'] },
        globalOffset: store.state.globalOffset || { x: 0, y: 0, z: 0 },
        // globalOffset: { x: 0, y: 0, z: 0 },
      };

      let root = doc.getRoot();

      // First try to find the specific view by guid
      let views = root.search({
        guid: viewId,
      });

      // If no specific view found, try to get default 3D view
      if (!views.length && viewId === 'default') {
        views = root.search({
          type: 'geometry',
        });
      }

      // If still no views, get any available viewable
      if (!views.length) {
        const viewables = doc.getRoot().getDefaultGeometry();
        if (viewables) {
          views = [viewables];
        }
      }

      if (views.length) {
        let view = views[0];

        this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, (event) => {
          resolve(event.model);
        }, { once: true });

        this.viewer
          .loadDocumentNode(doc, view, options)
          .then((model) => {
            this.loadedModels.set(modelKey, model);
            // resolve(model);
          })
          .catch((error) => {
            debugError(`Failed to load document node for ${modelKey}:`, error);
            reject(error);
          });
      } else {
        const error = new Error(`No suitable view found for viewId: ${viewId} in document`);
        debugError(error.message);
        reject(error);
      }
    });
  }

  onDocumentLoadFailure(viewerErrorCode) {
    debugError("Document load failed", viewerErrorCode);
  }

  setChangesPanelVisibility(visible) {
    if (this.diffToolExtension) {
      this.diffToolExtension.changesPanel.setVisible(visible);
    }
  }

  exitCompareMode() {
    try {
      // Set comparison mode to false
      store.state.isComparingMode = false;

      // Remove the exit button first
      this.removeExitCompareButton();

      // Unload the diff tool extension
      if (this.diffToolExtension) {
        this.viewer.unloadExtension("Autodesk.DiffTool");
        this.diffToolExtension = null;
      }

      // Reset original diff results
      this.originalDiffResults = null;

      // Hide all comparison models
      this.loadedModels.forEach((model) => {
        try {
          // Remove the model from the viewer
          if (store.state.mainUrn !== model.getSeedUrn()) {
            this.viewer.unloadModel(model);
          }
        } catch (error) {
          debugWarn('Error hiding comparison model:', error);
        }
      });

      // Restore original visible models
      if (this.defaultVisibleModels && this.defaultVisibleModels.length > 0) {
        this.defaultVisibleModels.forEach((model) => {
          try {
            this.viewer.showModel(model);
          } catch (error) {
            debugWarn('Error restoring original model:', error);
          }
        });
      } else {
        // If no original models to restore, show all models
        const allModels = this.viewer.getAllModels();
        allModels.forEach((model) => {
          if (!this.loadedModels.has(`${model.getDocumentNode()?.getDocument()?.getURN()}_${model.id}`)) {
            this.viewer.showModel(model);
          }
        });
      }

      // Reset default visible models
      this.defaultVisibleModels = [];

      // Clear loaded comparison models cache
      this.loadedModels.clear();

      // Clear performance caches
      this.clearPerformanceCaches();

      // Reset identity maps and model references
      this.identityMapAtoB = {};
      this.identityMapBtoA = {};
      this.modelA = null;
      this.modelB = null;
      this.modelObjectA = null;
      this.modelObjectB = null;

    } catch (error) {
      debugError('Error exiting compare mode:', error);
    }
  }

  // Expose this extension globally for debugging
  static exposeForDebugging(viewer) {
    const ext = viewer.getExtension('CompareToolExtension');
    if (ext) {
      window.compareExtension = ext;

      // Add performance debugging helpers
      window.compareExtension.debugPerformance = () => {
        const stats = ext.getPerformanceStats();
        console.table(stats);
        debugLog('Available performance methods:');
        debugLog('  - window.compareExtension.getPerformanceStats()');
        debugLog('  - window.compareExtension.configurePerformance(settings)');
        debugLog('  - window.compareExtension.clearPerformanceCaches()');
        debugLog('  - window.compareExtension.enableAccuracyMode() // Disable all optimizations');
        debugLog('  - window.compareExtension.enableSpeedMode() // Enable all optimizations');
        return stats;
      };

      // Helper methods for switching between accuracy and speed modes
      window.compareExtension.enableAccuracyMode = () => {
        ext.configurePerformance({ accuracyOverSpeed: true });
        debugLog('Accuracy mode enabled - all optimizations disabled for maximum precision');
      };

      window.compareExtension.enableSpeedMode = () => {
        ext.configurePerformance({
          accuracyOverSpeed: false,
          useGeometryCache: true,
          useSpatialIndex: true,
          enableParallelProcessing: true,
          hashBasedOptimization: true
        });
        debugLog('Speed mode enabled - all optimizations active');
      };
    }
  }

  // Detect translation-only modifications using identity property between two models
  async detectTranslationModificationsUsingIdentityProperty(modelA, modelB, propertyName, usePropertyDetection, tolerance = 0.01) {
    try {
      const [mapA, mapB] = await Promise.all([
        this.getIdentityMap(modelA, propertyName),
        this.getIdentityMap(modelB, propertyName)
      ]);

      const modifiedPairs = [];
      const modifiedTriplets = [];
      const modifiedA = [];
      const modifiedB = [];

      for (const extId in mapA) {
        const dbIdB = mapB[extId];
        if (!dbIdB) continue; // No counterpart in modelB

        // if (window.testExtIds && window.testExtIds.includes(extId)) {
        //   debugger;
        // }

        const dbIdA = mapA[extId];

        // Always skip these dbIds from added/removed lists
        modifiedA.push(dbIdA);
        modifiedB.push(dbIdB);

        // Determine changes
        const geomA = this.getObjectGeometry(modelA, dbIdA);
        const geomB = this.getObjectGeometry(modelB, dbIdB);

        if (!geomA || !geomB || !geomA.center || !geomB.center) continue;

        const distance = geomA.center.distanceTo(geomB.center);

        let propChanged = false;
        let changeType = 0;

        const equals = (a, b, tolerance = 0.001) => {
          return Math.abs(a.x - b.x) <= tolerance && Math.abs(a.y - b.y) <= tolerance && Math.abs(a.z - b.z) <= tolerance;
        }

        if (!equals(geomA.size, geomB.size)) {
          changeType |= ChangeType.GeomChange;
        } else if (distance > tolerance) {
          changeType |= ChangeType.XformChange;
        }

        if (changeType === 0 && usePropertyDetection) {
          // Only check properties if no translation change (to save cost)
          const propertiesA = await this.getProperties(modelA, dbIdA);
          const propertiesB = await this.getProperties(modelB, dbIdB);

          propChanged = this.hasDifferentProperties(propertiesA, propertiesB);

          if (propChanged) {
            changeType |= ChangeType.PropChange;
          }
        }

        if (changeType !== 0) {
          modifiedPairs.push({ a: dbIdA, b: dbIdB });
          modifiedTriplets.push(dbIdA, dbIdB, changeType);
        }
      }

      // After processing all identifiers, cache quick lookup maps
      this.identityMapAtoB = {};
      this.identityMapBtoA = {};
      for (const extId in mapA) {
        const aId = mapA[extId];
        const bId = mapB[extId];
        if (aId && bId) {
          this.identityMapAtoB[aId] = bId;
          this.identityMapBtoA[bId] = aId;
        }
      }
      return { modifiedPairs, modifiedTriplets, modifiedA, modifiedB };

    } catch (err) {
      debugWarn('Identity property translation detection failed:', err);
      return { modifiedPairs: [], modifiedTriplets: [], modifiedA: [], modifiedB: [] };
    }
  }

  // Returns a map of propertyKey -> propertyObject similar to DesignDiff.GetProperties
  async getProperties(model, dbId) {
    const dbIds = Array.isArray(dbId) ? dbId : [dbId];

    return new Promise((resolve) => {
      const buildDict = (result) => {
        const dict = {};
        if (result && result.properties) {
          result.properties.forEach((p) => {
            if (p.hidden) return;
            if (this.shouldIgnoreProperty(p, model)) return;
            if (p.displayName) {
              dict[`${p.displayCategory}#${p.displayName}`] = p;
            }
          });
        }
        return dict;
      };

      const onSuccess = (results) => {
        const first = Array.isArray(results) ? results[0] : results;
        const filtered = first.properties.filter((p) => !p.hidden && !this.shouldIgnoreProperty(p, model));
        resolve(buildDict({ properties: filtered }));
      };

      const onError = () => resolve({});

      if (typeof model.getBulkProperties2 === 'function') {
        model.getBulkProperties2(dbIds, { ignoreHidden: true }, onSuccess, onError);
      } else if (typeof model.getBulkProperties === 'function') {
        model.getBulkProperties(dbIds, { ignoreHidden: true }, onSuccess, onError);
      } else {
        // Fallback to single-object API
        model.getProperties(dbIds[0], ({ properties }) => {
          const filtered = properties.filter((p) => !p.hidden && !this.shouldIgnoreProperty(p, model));
          resolve(buildDict({ properties: filtered }));
        }, onError);
      }
    });
  }

  // Determine if a property should be ignored similar to DesignDiff rules
  shouldIgnoreProperty(prop, model) {
    let mimeType = this.getMimeTypeFromModel(model) || this.currentMimeType || '';

    const isMaterial = (p) => {
      return ['Material', 'Revit Material', 'Autodesk Material'].includes(p.displayCategory) ||
        (p.displayCategory === 'Item' && p.displayName === 'Material');
    };

    const isLevel = (p) => {
      return ['Level', 'Schedule Level'].includes(p.displayCategory);
    };

    // Skip if filtered by mime–specific rules
    switch (mimeType) {
      case 'application/vnd.autodesk.cad':
        return isMaterial(prop) ||
          (prop.displayCategory === 'Item' && prop.displayName === 'Icon') ||
          (prop.displayCategory === 'Electrical - Lighting' && prop.displayName === 'Coefficient of Utilization') ||
          (prop.displayCategory === 'Item' && prop.displayName === 'GUID') ||
          (prop.displayCategory === 'Item' && prop.displayName === 'Source File') ||
          prop.displayName === 'GLOBALID';

      case 'application/vnd.autodesk.navisworks':
        return isMaterial(prop) || isLevel(prop) ||
          (prop.displayCategory === 'Item' && prop.displayName === 'Icon') ||
          (prop.displayCategory === 'Element' && prop.displayName === 'Coefficient of Utilization') ||
          (prop.displayCategory === 'Item' && prop.displayName === 'Source File') ||
          prop.displayCategory in (model.roomNames || {});

      case 'application/vnd.autodesk.r360':
      case 'application/vnd.autodesk.revit':
        return (prop.displayCategory === 'Electrical - Lighting' && prop.displayName === 'Coefficient of Utilization');

      default:
        return false;
    }
  }

  // Derive mime type from model metadata or file extension
  getMimeTypeFromModel(model) {
    if (!model || typeof model.getData !== 'function') return '';

    const data = model.getData();

    const ext = (data?.loadOptions?.fileExt || '').toLowerCase();
    const map = {
      // Revit / Navisworks
      rvt: 'application/vnd.autodesk.revit',
      nwd: 'application/vnd.autodesk.navisworks',
      nwc: 'application/vnd.autodesk.navisworks',

      // Fusion / Inventor & generic Autodesk CAD
      f3d: 'application/vnd.autodesk.f3d',
      f2d: 'application/vnd.autodesk.f3d',
      ipt: 'application/vnd.autodesk.cad',
      iam: 'application/vnd.autodesk.inventor.assembly',
      cad: 'application/vnd.autodesk.cad',

      // AutoCAD family
      dwg: 'application/vnd.autodesk.autocad.dwg',
      dxf: 'application/vnd.autodesk.dxf',
      dwt: 'application/vnd.autodesk.autocad.dwg',

      // STEP / STP
      step: 'application/vnd.autodesk.step',
      stp: 'application/vnd.autodesk.step',

      // IFC (treated as generic CAD)
      ifc: 'application/vnd.autodesk.cad',

      // OBJ/STL (mesh formats – treat as CAD for diff purposes)
      obj: 'application/vnd.autodesk.cad',
      stl: 'application/vnd.autodesk.cad',

      // Misc.
      fbx: 'application/vnd.autodesk.cad'
    };
    return map[ext] || '';
  }

  // --- Property comparison helpers (adapted from DesignDiff) ---

  diffProperties(key, dictA, dictB) {
    const propA = dictA[key];
    const propB = dictB[key];

    if (propA && propB) {
      let a = propA.displayValue;
      let b = propB.displayValue;

      const aNumeric = propA.type === 2 || propA.type === 3;
      const bNumeric = propB.type === 2 || propB.type === 3;

      if (aNumeric !== bNumeric) {
        if (aNumeric && propB.type === 20) {
          b = parseFloat(b);
        } else if (bNumeric && propA.type === 20) {
          a = parseFloat(a);
        }
      }

      if (Number.isFinite(a) && Number.isFinite(b)) {
        return Math.abs(a - b) > 1.0e-8;
      }
      return a !== b;
    }

    if (propA) {
      return propA.displayValue !== '';
    }

    if (propB) {
      return propB.displayValue !== '';
    }

    return false;
  }

  hasDifferentProperties(dictA, dictB) {
    if (!dictA || !dictB) return true;

    // if (Object.keys(dictA).length !== Object.keys(dictB).length) {
    //   return true;
    // }

    return Object.keys(dictA).some((key) => this.diffProperties(key, dictA, dictB));
  }

  // Quick helper to get counterpart dbId across models after comparison
  // direction: 'AtoB' (default) or 'BtoA'
  getCorrespondingDbId(sourceDbId, direction = 'AtoB') {
    if (direction === 'AtoB') {
      return this.identityMapAtoB ? this.identityMapAtoB[sourceDbId] ?? null : null;
    }
    if (direction === 'BtoA') {
      return this.identityMapBtoA ? this.identityMapBtoA[sourceDbId] ?? null : null;
    }
    debugWarn('getCorrespondingDbId: Invalid direction supplied. Use "AtoB" or "BtoA".');
    return null;
  }

  // Toggle viewer ghosting when compare tool is active
  toggleCompareGhosting() {
    if (this.diffToolExtension) {
      const ghosting = this.viewer.prefs.get('ghosting');
      this.viewer.setGhosting(!ghosting);
    }
  }

  // ----------------------------
  // Identity-mapping helpers
  // ----------------------------

  addIdentityMapping(dbIdA, dbIdB) {
    if (dbIdA == null || dbIdB == null) return;
    if (!this.identityMapAtoB) this.identityMapAtoB = {};
    if (!this.identityMapBtoA) this.identityMapBtoA = {};

    if (!this.identityMapAtoB[dbIdA]) {
      this.identityMapAtoB[dbIdA] = dbIdB;
    }
    if (!this.identityMapBtoA[dbIdB]) {
      this.identityMapBtoA[dbIdB] = dbIdA;
    }
  }

  // Return dbId of first geometry in the target index that matches geomSource
  getGeometricMatchDbId(geomSource, targetIndex) {
    if (!geomSource || !targetIndex) return null;

    // 1) Try hash-based lookup for quick exact matches.
    if (this.performanceSettings.hashBasedOptimization && geomSource.hashes.length > 0) {
      const validHashes = geomSource.hashes.filter(h => h !== 'no-hash');
      if (validHashes.length > 0) {
        const hashKey = validHashes.sort().join('|');
        if (targetIndex.hashIndex.has(hashKey)) {
          const candidates = targetIndex.hashIndex.get(hashKey);
          for (const candidate of candidates) {
            if (this.compareGeometriesByMode(geomSource, candidate.geom)) {
              return candidate.dbId;
            }
          }
        }
      }
    }

    // 2) Fallback to size-bucket fuzzy matching
    const sizeKeys = this.getFuzzySizeKeys(geomSource.size);
    const visited = new Set();
    for (const sizeKey of sizeKeys) {
      const bucket = targetIndex.spatialIndex.get(sizeKey) || [];
      for (const candidate of bucket) {
        if (visited.has(candidate.dbId)) continue;
        visited.add(candidate.dbId);
        if (this.compareGeometriesByMode(geomSource, candidate.geom)) {
          return candidate.dbId;
        }
      }
    }

    return null;
  }

  // Retrieve a mapping of unique identity property values (e.g. externalId) to dbId for the given model.
  async getIdentityMap(model, propertyName) {
    const dbIds = this.getAllDbIds(model);

    return new Promise((resolve) => {
      const buildMap = (results) => {
        const valueCounts = {};
        results.forEach((r) => {
          let propVal = null;
          if (propertyName === 'externalId' && r.externalId) {
            propVal = r.externalId;
          } else if (r.properties && r.properties.length) {
            const found = r.properties.find((p) => p.displayName === propertyName);
            if (found) propVal = found.displayValue;
          }
          if (propVal) {
            valueCounts[propVal] = (valueCounts[propVal] || 0) + 1;
          }
        });

        const map = {};
        results.forEach((r) => {
          let propVal = null;
          if (propertyName === 'externalId' && r.externalId) {
            propVal = r.externalId;
          } else if (r.properties && r.properties.length) {
            const found = r.properties.find((p) => p.displayName === propertyName);
            if (found) propVal = found.displayValue;
          }
          if (propVal && valueCounts[propVal] === 1) {
            map[propVal] = r.dbId;
          }
        });

        resolve(map);
      };

      const onError = () => resolve({});

      if (typeof model.getBulkProperties2 === 'function') {
        model.getBulkProperties2(dbIds, { propFilter: [propertyName], ignoreHidden: true }, buildMap, onError);
      } else if (typeof model.getBulkProperties === 'function') {
        model.getBulkProperties(dbIds, [propertyName], buildMap, onError);
      } else {
        // Fallback: iterate one by one (slower)
        const promises = dbIds.map((id) => this.getProperties(model, id).then((props) => ({ id, props })));
        Promise.all(promises).then((entries) => {
          const results = entries.map((e) => ({
            dbId: e.id,
            properties: Object.values(e.props),
            externalId: null,
          }));
          buildMap(results);
        });
      }
    });
  }

  isolateInVersion1(dbIds, options = {}) {
    if (!this.modelA) {
      debugWarn('Model A not available for isolation');
      return;
    }

    const {
      isolateOnly = false,  // If true, only isolate without selection/zoom
      fitToView = true,     // Whether to fit view to elements
      selectElements = true // Whether to select the elements
    } = options;

    try {
      if (!dbIds || dbIds.length === 0) {
        // Clear isolation and selection
        this.viewer.isolate([], this.modelA);
        this.viewer.clearSelection();
      } else {
        if (!isolateOnly) {
          // Select the elements first for highlighting
          if (selectElements) {
            this.viewer.select(dbIds, this.modelA);
          }

          // Fit view to specific elements rather than isolating everything
          if (fitToView) {
            this.viewer.fitToView(dbIds, this.modelA);
          }
        } else {
          // Traditional isolation approach
          this.viewer.isolate(dbIds, this.modelA);
          if (fitToView) {
            this.viewer.fitToView();
          }
        }
      }

      // Invalidate to refresh the view
      this.viewer.impl.invalidate(true);

    } catch (error) {
      debugError('Error handling elements in Version 1:', error);
    }
  }

  isolateInVersion2(dbIds, options = {}) {
    if (!this.modelB) {
      debugWarn('Model B not available for isolation');
      return;
    }

    const {
      isolateOnly = false,  // If true, only isolate without selection/zoom
      fitToView = true,     // Whether to fit view to elements
      selectElements = true // Whether to select the elements
    } = options;

    try {
      if (!dbIds || dbIds.length === 0) {
        // Clear isolation and selection
        this.viewer.isolate([], this.modelB);
        this.viewer.clearSelection();
      } else {
        if (!isolateOnly) {
          // Select the elements first for highlighting
          if (selectElements) {
            this.viewer.select(dbIds, this.modelB);
          }

          // Fit view to specific elements rather than isolating everything
          if (fitToView) {
            this.viewer.fitToView(dbIds, this.modelB);
          }
        } else {
          // Traditional isolation approach
          this.viewer.isolate(dbIds, this.modelB);
          if (fitToView) {
            this.viewer.fitToView();
          }
        }
      }

      // Invalidate to refresh the view
      this.viewer.impl.invalidate(true);

    } catch (error) {
      debugError('Error handling elements in Version 2:', error);
    }
  }

  // Convenience methods for common use cases
  zoomToModifiedInVersion1(dbIds) {
    this.isolateInVersion1(dbIds, {
      isolateOnly: false,
      fitToView: true,
      selectElements: true
    });
  }

  zoomToModifiedInVersion2(dbIds) {
    this.isolateInVersion2(dbIds, {
      isolateOnly: false,
      fitToView: true,
      selectElements: true
    });
  }

  // Focus on specific changes without losing model context
  highlightChanges(addedIds, removedIds, modifiedIds) {
    try {
      // Clear any existing selection
      this.viewer.clearSelection();

      if (addedIds && addedIds.length > 0 && this.modelA) {
        // Highlight added elements in green in Version 1
        this.viewer.setThemingColor(addedIds, new THREE.Vector4(0, 1, 0, 0.7), this.modelA);
        this.zoomToModifiedInVersion1(addedIds); // Zoom to first 5 for performance
      }

      if (removedIds && removedIds.length > 0 && this.modelB) {
        // Highlight removed elements in red in Version 2  
        this.viewer.setThemingColor(removedIds, new THREE.Vector4(1, 0, 0, 0.7), this.modelB);
        if (!addedIds || addedIds.length === 0) {
          this.zoomToModifiedInVersion2(removedIds);
        }
      }

      if (modifiedIds && modifiedIds.length > 0) {
        // Highlight modified elements in yellow in both models
        const yellowColor = new THREE.Vector4(1, 1, 0, 0.7);

        if (this.modelA) {
          const modifiedInA = modifiedIds.filter(id => this.getAllDbIds(this.modelA).includes(id));
          if (modifiedInA.length > 0) {
            this.viewer.setThemingColor(modifiedInA, yellowColor, this.modelA);
            if (!addedIds?.length && !removedIds?.length) {
              this.zoomToModifiedInVersion1(modifiedInA);
            }
          }
        }

        if (this.modelB) {
          const modifiedInB = modifiedIds.filter(id => this.getAllDbIds(this.modelB).includes(id));
          if (modifiedInB.length > 0) {
            this.viewer.setThemingColor(modifiedInB, yellowColor, this.modelB);
          }
        }
      }

    } catch (error) {
      debugError('Error highlighting changes:', error);
    }
  }
}

Autodesk.Viewing.theExtensionManager.registerExtension(
  CompareToolExtension.extensionId,
  CompareToolExtension
);

export default CompareToolExtension;
